import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import {
  Package,
  Truck,
  RotateCcw,
  Search,
  SearchX,
  Inbox,
  AlertTriangle,
  Send,
} from "lucide-react";
import { previewWhatsappAlerts } from "api/packages";
import { getActiveDeliveryUsers } from "api/user";
import { getChannelIntegrations } from "api/channelIntegrations";
import { createWhatsappBroadcast } from "api/whatsappBroadcasts";
import { Modal } from "components/ui/Modal";
import BroadcastComposer from "./whatsapp/BroadcastComposer";
import BroadcastActivity from "./whatsapp/BroadcastActivity";
import {
  BROADCAST_MODES,
  BROADCAST_SCOPES,
  BROADCAST_TEXT_MAX_LENGTH,
  broadcastNumberOptions,
  buildBroadcastPayload,
  newIdempotencyKey,
  renderPreview,
  templateTextFor,
  unknownVariablesIn,
} from "./whatsapp/broadcastMessage";
import { showToast } from "utils/showToast";
import { Card } from "components/ui/Card";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import { Badge } from "components/ui/Badge";
import { EmptyState } from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import { SearchableSelect } from "components/ui/SearchableSelect";
import { Table, THead, TBody, TR, TH, TD } from "components/ui/Table";
import { useIsMobile } from "hooks/useIsMobile";
import useAuthScope from "hooks/useAuthScope";
import { cn } from "utils/cn";

const STATUS_OPTIONS_CONFIG = [
  { value: "RECOLECTADO", labelKey: "Recolectado" },
  { value: "EN_RUTA", labelKey: "En Ruta" },
  { value: "ENTREGA_FALLIDA", labelKey: "Entrega fallida" },
];

// El backend (microservice-packages) espera `statuses` como códigos numéricos de
// StatusBeartrackEnum, no las etiquetas de grupo de la UI. Cada grupo expande a uno o
// más códigos; este mapeo debe reflejar getStatusLabel/resolveWhatsappStatusFilters del
// servicio: si allá cambia la agrupación, hay que actualizarlo aquí también.
const STATUS_GROUP_TO_CODES = {
  RECOLECTADO: [1], // COLLECTED
  EN_RUTA: [3, 30], // SCANNED_TO_ROUTE, NAVIGATING_TO_ADDRESS
  ENTREGA_FALLIDA: [6, 7], // INCORRECT_ADDRESS, NO_INHABITANTS
};

const expandStatusGroups = (groups) =>
  Array.from(new Set((groups || []).flatMap((g) => STATUS_GROUP_TO_CODES[g] || [])));

const TYPE_OPTIONS_CONFIG = [
  { value: "MERCADOLIBRE", labelKey: "MercadoLibre" },
  { value: "OTROS", labelKey: "Todo el resto" },
];

const SCOPES_CONFIG = [
  {
    id: BROADCAST_SCOPES.FILTERS,
    labelKey: "Todos los filtrados",
    helperKey: "Todos los paquetes que cumplen los filtros, no solo esta página",
  },
  {
    id: BROADCAST_SCOPES.SELECTION,
    labelKey: "Solo los seleccionados",
    helperKey: "Los paquetes marcados en la tabla",
  },
];

// Tope de destinatarios por comunicación (MAX_RECIPIENTS_PER_BROADCAST del backend).
const MAX_RECIPIENTS = 5000;
// Supuestos de la estimación que se muestra antes de confirmar: cupo diario por número
// (SEND_DIRECT_BROADCAST_DAILY_CAP de conversations) y un mensaje cada ~5 s.
const ESTIMATE_DAILY_CAP = 500;
const ESTIMATE_SECONDS_PER_MESSAGE = 5;

// El backend responde 400 con este texto cuando los filtros no dejan ningún
// paquete elegible. Es un resultado normal de la búsqueda, no un fallo: va como
// aviso ámbar y no como error rojo.
const NO_PACKAGES_MESSAGE = "No se encontraron paquetes que cumplan con los criterios";

// La vista previa (y el envío "todos") exigen una de las dos fechas en el
// payload: sin ninguna, el backend responde 400 "Debe proporcionar al menos
// collectDateFrom o updatedAtFrom". El envío por packageIds no las necesita.
const hasNoDateFilter = (payload) =>
  !payload?.collectDateFrom && !payload?.updatedAtFrom;

const DEFAULT_FILTERS = {
  statuses: STATUS_OPTIONS_CONFIG.map((o) => o.value),
  packageType: TYPE_OPTIONS_CONFIG[1].value,
  senders: [],
  municipalities: [],
  collectDateFrom: "",
  deliveryId: "",
  updatedAtFrom: "",
  updatedAtTo: "",
  page: 1,
  limit: 50,
};

// react-select themed via tokens (mirrors ui/SearchableSelect) — supports isMulti.
const selectClassNames = {
  control: ({ isFocused }) =>
    cn(
      "flex items-center min-h-[40px] rounded-lg border bg-bg text-sm transition-colors",
      isFocused ? "border-transparent ring-2 ring-brand-500" : "border-border"
    ),
  valueContainer: () => "flex flex-wrap items-center gap-0.5 pl-2 pr-1 grow",
  placeholder: () => "text-fg-muted pl-1",
  singleValue: () => "text-fg pl-1",
  multiValue: () => "flex items-center gap-1 rounded-md bg-brand-50 pl-2 pr-1 my-0.5 text-brand-700",
  multiValueLabel: () => "text-xs text-brand-700 py-0.5",
  multiValueRemove: () => "cursor-pointer rounded hover:bg-brand-100 [&>svg]:w-3.5 [&>svg]:h-3.5",
  input: () => "text-fg [&_input]:focus:ring-0 [&_input]:shadow-none",
  indicatorsContainer: () => "flex items-center text-fg-muted",
  dropdownIndicator: () => "px-1.5 [&>svg]:w-4 [&>svg]:h-4",
  clearIndicator: () => "px-1 cursor-pointer hover:text-fg [&>svg]:w-4 [&>svg]:h-4",
  indicatorSeparator: () => "hidden",
  menu: () => "mt-1 rounded-lg border border-border bg-bg shadow-elevated overflow-hidden z-[10000]",
  menuList: () => "py-1 max-h-60 overflow-y-auto",
  option: ({ isFocused, isSelected }) =>
    cn(
      "px-3 py-2 text-sm cursor-pointer",
      isSelected ? "bg-brand-500 text-white" : isFocused ? "bg-brand-50 text-brand-700" : "text-fg"
    ),
  noOptionsMessage: () => "px-3 py-2 text-sm text-fg-muted",
};
const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 10000 }) };

function TokenSelect(props) {
  return (
    <ReactSelect
      unstyled
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      classNames={selectClassNames}
      styles={selectStyles}
      {...props}
    />
  );
}

export default function WhatsappAlertsPanel() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { logisticCompanyId } = useAuthScope();
  const STATUS_OPTIONS = STATUS_OPTIONS_CONFIG.map((o) => ({ value: o.value, label: t(o.labelKey) }));
  const TYPE_OPTIONS = TYPE_OPTIONS_CONFIG.map((o) => ({ value: o.value, label: t(o.labelKey) }));
  const SCOPES = SCOPES_CONFIG.map((s) => ({ ...s, label: t(s.labelKey), helper: t(s.helperKey) }));

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [filterOptions, setFilterOptions] = useState({ senders: [], municipalities: [] });
  const [preview, setPreview] = useState(null);
  // Filtros de la ÚLTIMA búsqueda: "todos los filtrados" envía estos, no lo que se haya
  // editado después en la barra sin volver a buscar.
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState(BROADCAST_SCOPES.FILTERS);
  const [message, setMessage] = useState({ integrationId: "", mode: BROADCAST_MODES.ORDEN_DEMORADA, text: "" });
  const [numbers, setNumbers] = useState({ options: [], loading: true, error: null });
  const [confirm, setConfirm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeBroadcastId, setActiveBroadcastId] = useState(null);
  const [historyToken, setHistoryToken] = useState(0);
  const [showExcluded, setShowExcluded] = useState(false);
  const [driverOptions, setDriverOptions] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let mounted = true;
    setLoadingDrivers(true);
    getActiveDeliveryUsers({ logisticCompanyId, limit: 1000 }).then(
      ({ success, data, error }) => {
        if (!mounted) return;
        if (!success) {
          showToast(error?.message || t("No se pudieron cargar los repartidores"), false);
          setLoadingDrivers(false);
          return;
        }
        const docs = data?.docs || [];
        setDriverOptions(
          docs.map((d) => ({
            value: d._id || d.id,
            label: `${d.name || t("Sin nombre")} ${d.lastName || ""}`.trim(),
          }))
        );
        setLoadingDrivers(false);
      }
    );
    return () => { mounted = false; };
  }, [logisticCompanyId]);

  // Números de WhatsApp del operador (microservice-conversations). Solo los no oficiales
  // de notificaciones o ambos; se preselecciona si hay uno solo utilizable.
  useEffect(() => {
    let mounted = true;
    getChannelIntegrations({ channel: "whatsapp_unofficial" }).then((response) => {
      if (!mounted) return;
      if (response?.error) {
        // `true` = falló sin texto propio: el composer muestra su mensaje traducido.
        setNumbers({ options: [], loading: false, error: response.error.message || true });
        return;
      }
      const options = broadcastNumberOptions(response?.integrations);
      setNumbers({ options, loading: false, error: null });
      const usable = options.filter((o) => o.usable);
      if (usable.length === 1) {
        setMessage((prev) => (prev.integrationId ? prev : { ...prev, integrationId: usable[0].id }));
      }
    });
    return () => { mounted = false; };
  }, []);

  const sampleItem = useMemo(() => {
    if (!preview?.items?.length) return null;
    const firstSelected = preview.items.find((item) => selectedIds.has(item.id));
    return firstSelected || preview.items[0];
  }, [preview, selectedIds]);

  // La API ya no tostea sus errores: se muestran acá UNA vez. El 400 de "sin
  // paquetes" se trata como aviso (no hay nada roto, simplemente no hay match).
  const showAlertError = (error, fallbackMessage) => {
    if (error?.message === NO_PACKAGES_MESSAGE) {
      showToast(t(NO_PACKAGES_MESSAGE), "warning");
      return;
    }
    showToast(error?.message || fallbackMessage, false);
  };

  const fetchPreview = async (payload) => {
    if (hasNoDateFilter(payload)) {
      showToast(t("Indica una fecha de colecta o de actualización."), "warning");
      return;
    }
    setLoading(true);
    const response = await previewWhatsappAlerts({
      ...payload,
      statuses: expandStatusGroups(payload.statuses),
    });
    setLoading(false);

    if (!response.success) {
      showAlertError(response.error, t("No se pudo cargar la vista previa"));
      return;
    }

    const data = response.data;
    setPreview(data);
    setFilters({ ...payload });
    setAppliedFilters({ ...payload });
    setFilterOptions({
      senders: (data?.availableFilters?.senders || []).map((value) => ({ value, label: value })),
      municipalities: (data?.availableFilters?.municipalities || []).map((value) => ({ value, label: value })),
    });
    setSelectedIds(new Set((data?.items || []).map((item) => item.id)));
    setShowExcluded(false);
  };

  const handleApplyFilters = () => fetchPreview({ ...filters, page: 1 });

  // Limpiar NO vuelve a consultar: DEFAULT_FILTERS no trae fechas, así que el
  // backend respondería 400 siempre. La vista previa queda vacía hasta que el
  // usuario elija rango y pulse "Buscar".
  const handleResetFilters = () => {
    setScope(BROADCAST_SCOPES.FILTERS);
    setFilters({ ...DEFAULT_FILTERS });
    setAppliedFilters(null);
    setPreview(null);
    setSelectedIds(new Set());
    setShowExcluded(false);
  };

  const handleToggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (preview?.items?.length) {
      setSelectedIds(new Set(preview.items.map((item) => item.id)));
    }
  };

  const selectedCount = selectedIds.size;
  const totalDocs = preview?.pagination?.totalDocs || 0;
  const totalEligible = preview?.pagination?.totalEligible || 0;
  const recipientsCount = scope === BROADCAST_SCOPES.SELECTION ? selectedCount : totalDocs;

  /** Motivo por el que todavía no se puede enviar (o null si se puede). */
  const blockedReason = (() => {
    if (!preview || !appliedFilters) return t("Primero busca paquetes con los filtros.");
    const number = numbers.options.find((o) => o.id === message.integrationId);
    if (!number?.usable) return t("Elige un número de WhatsApp conectado.");
    if (message.mode === BROADCAST_MODES.FREE_TEXT) {
      const text = (message.text || "").trim();
      if (!text) return t("Escribe el mensaje.");
      if (text.length > BROADCAST_TEXT_MAX_LENGTH) return t("El mensaje es demasiado largo.");
      if (unknownVariablesIn(text).length) return t("Corrige las variables desconocidas.");
    }
    if (!recipientsCount) return t("No hay destinatarios para enviar.");
    if (recipientsCount > MAX_RECIPIENTS) {
      return t("Máximo {{max}} destinatarios por comunicación. Acota los filtros.", { max: MAX_RECIPIENTS });
    }
    return null;
  })();

  // La clave de idempotencia se genera al ABRIR la confirmación y se reutiliza si el envío
  // se reintenta desde el mismo diálogo: un doble clic o un reintento tras un corte de red
  // devuelven la misma comunicación en vez de duplicarla.
  const handleOpenConfirm = () => {
    if (blockedReason) {
      showToast(blockedReason, "warning");
      return;
    }
    setConfirm({ idempotencyKey: newIdempotencyKey() });
  };

  const handleConfirmSend = async () => {
    setCreating(true);
    const response = await createWhatsappBroadcast(
      buildBroadcastPayload({
        scope,
        filters: { ...appliedFilters, statuses: expandStatusGroups(appliedFilters.statuses) },
        selectedIds,
        integrationId: message.integrationId,
        mode: message.mode,
        text: message.text,
        idempotencyKey: confirm.idempotencyKey,
      })
    );
    setCreating(false);
    if (!response.success) {
      showAlertError(response.error, t("No se pudo crear la comunicación"));
      return;
    }
    setConfirm(null);
    const { broadcastId, totalRecipients, excludedCount } = response.data || {};
    showToast(
      t("Comunicación en curso: {{count}} destinatarios ({{excluded}} excluidos).", {
        count: totalRecipients || 0,
        excluded: excludedCount || 0,
      }),
      true
    );
    setActiveBroadcastId(broadcastId);
    setHistoryToken((n) => n + 1);
  };

  const estimateDays = Math.max(1, Math.ceil(recipientsCount / ESTIMATE_DAILY_CAP));
  const estimateMinutes = Math.ceil(
    (Math.min(recipientsCount, ESTIMATE_DAILY_CAP) * ESTIMATE_SECONDS_PER_MESSAGE) / 60
  );
  const selectedNumberLabel = numbers.options.find((o) => o.id === message.integrationId)?.label;

  const fieldLabel = "text-xs font-semibold text-fg-muted";
  const groupTitle =
    "flex items-center gap-1.5 text-[0.72rem] font-bold uppercase tracking-wider text-fg-subtle";

  const stats = preview
    ? [
        { value: totalDocs, label: t("filtrados"), tone: "neutral" },
        { value: totalEligible, label: t("elegibles"), tone: "success" },
        { value: selectedCount, label: t("seleccionados"), tone: "brand" },
      ]
    : [];

  const statValueTone = {
    neutral: "text-fg",
    success: "text-status-success",
    brand: "text-brand-600",
  };

  const confirmTemplate = templateTextFor(message.mode, message.text);

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      <Modal
        open={!!confirm}
        onClose={() => !creating && setConfirm(null)}
        size="md"
        title={t("Confirmar envío por WhatsApp")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={creating}>
              {t("Volver")}
            </Button>
            <Button onClick={handleConfirmSend} loading={creating} disabled={creating} iconLeft={<Send className="w-4 h-4" />}>
              {creating ? t("Creando...") : t("Enviar")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm">
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-fg-muted">{t("Desde")}</dt>
            <dd className="m-0 font-semibold text-fg">{selectedNumberLabel || "—"}</dd>
            <dt className="text-fg-muted">{t("Destinatarios")}</dt>
            <dd className="m-0 font-semibold text-fg">
              {t("hasta {{count}}", { count: recipientsCount })}
            </dd>
            <dt className="text-fg-muted">{t("Duración estimada")}</dt>
            <dd className="m-0 text-fg">
              {estimateDays > 1
                ? t("{{days}} días (cupo de {{cap}} por día)", { days: estimateDays, cap: ESTIMATE_DAILY_CAP })
                : t("~{{minutes}} min dentro del horario", { minutes: estimateMinutes })}
            </dd>
          </dl>
          <p className="m-0 text-xs text-fg-muted">
            {t("Se excluyen los paquetes sin teléfono válido o sin los datos que usa el mensaje. Si un paquete cambia de estado antes de su turno (por ejemplo, se entrega), no se le envía.")}
          </p>
          <div className="rounded-[2px_14px_14px_14px] bg-status-success-soft p-3">
            <p className="m-0 whitespace-pre-wrap break-words leading-relaxed text-fg">
              {renderPreview(confirmTemplate, sampleItem)}
            </p>
          </div>
        </div>
      </Modal>

      {/* ── Filter bar ── */}
      <Card className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex min-w-[280px] flex-1 flex-col gap-2.5 rounded-xl border border-border bg-bg-muted p-3.5">
            <span className={groupTitle}>
              <Package className="w-4 h-4" />
              {t("Paquete")}
            </span>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2.5">
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Estado")}</span>
                <TokenSelect
                  isMulti
                  placeholder={t("Todos")}
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.filter((o) => filters.statuses.includes(o.value))}
                  onChange={(vals) =>
                    setFilters((prev) => ({ ...prev, statuses: (vals || []).map((v) => v.value) }))
                  }
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Tipo")}</span>
                <SearchableSelect
                  options={TYPE_OPTIONS}
                  placeholder={t("Tipo")}
                  value={filters.packageType}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, packageType: value || TYPE_OPTIONS_CONFIG[1].value }))
                  }
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Cliente remitente")}</span>
                <TokenSelect
                  isMulti
                  placeholder={t("Todos")}
                  options={filterOptions.senders}
                  value={filterOptions.senders.filter((o) => filters.senders.includes(o.value))}
                  onChange={(vals) =>
                    setFilters((prev) => ({ ...prev, senders: (vals || []).map((v) => v.value) }))
                  }
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Comuna")}</span>
                <TokenSelect
                  isMulti
                  placeholder={t("Todas")}
                  options={filterOptions.municipalities}
                  value={filterOptions.municipalities.filter((o) => filters.municipalities.includes(o.value))}
                  onChange={(vals) =>
                    setFilters((prev) => ({ ...prev, municipalities: (vals || []).map((v) => v.value) }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex min-w-[280px] flex-1 flex-col gap-2.5 rounded-xl border border-border bg-bg-muted p-3.5">
            <span className={groupTitle}>
              <Truck className="w-4 h-4" />
              {t("Operación")}
            </span>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2.5">
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Repartidor")}</span>
                <SearchableSelect
                  placeholder={loadingDrivers ? t("Cargando...") : t("Todos")}
                  isClearable
                  isDisabled={loadingDrivers}
                  options={driverOptions}
                  value={filters.deliveryId}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, deliveryId: value || "" }))
                  }
                  noOptionsText={t("Sin resultados")}
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Recolección desde")}</span>
                <Input
                  type="date"
                  value={filters.collectDateFrom || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, collectDateFrom: e.target.value }))}
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Actualización desde")}</span>
                <Input
                  type="date"
                  value={filters.updatedAtFrom || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, updatedAtFrom: e.target.value }))}
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className={fieldLabel}>{t("Actualización hasta")}</span>
                <Input
                  type="date"
                  value={filters.updatedAtTo || ""}
                  onChange={(e) => setFilters((prev) => ({ ...prev, updatedAtTo: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetFilters}
            disabled={loading}
            iconLeft={<RotateCcw className="w-4 h-4" />}
          >
            {t("Limpiar")}
          </Button>
          <Button
            size="sm"
            onClick={handleApplyFilters}
            disabled={loading}
            loading={loading}
            iconLeft={<Search className="w-4 h-4" />}
          >
            {loading ? t("Buscando...") : t("Buscar")}
          </Button>
        </div>
      </Card>

      {/* ── Stats ── */}
      {preview && (
        <div className="flex flex-wrap gap-2.5">
          {stats.map((s) => (
            <div
              key={s.label}
              className="inline-flex items-baseline gap-1.5 rounded-full border border-border bg-bg px-3.5 py-1.5 text-sm shadow-card"
            >
              <strong className={cn("text-base tabular-nums", statValueTone[s.tone])}>{s.value}</strong>
              <span className="text-fg-muted">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Main grid: table | send ── */}
      <div className="grid grid-cols-1 gap-5 items-start xl:grid-cols-[minmax(0,1fr)_280px]">
        {/* Table card */}
        <Card className="flex flex-col gap-3">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={!preview?.items?.length}
            >
              {t("Seleccionar todos")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={!preview?.items?.length}
            >
              {t("Limpiar selección")}
            </Button>
          </div>

          {loading ? (
            <div className="flex flex-col gap-2 py-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : !preview ? (
            <EmptyState
              icon={<SearchX />}
              title={t("Sin resultados aún")}
              description={t('Aplica filtros y presiona "Buscar" para ver los paquetes.')}
            />
          ) : !preview?.items?.length ? (
            <EmptyState
              icon={<Inbox />}
              title={t("Sin paquetes")}
              description={t("Sin paquetes para los filtros actuales.")}
            />
          ) : isMobile ? (
            <ul className="flex list-none flex-col gap-2.5 p-0 m-0">
              {preview.items.map((item) => {
                const checked = selectedIds.has(item.id);
                return (
                  <li
                    key={item.id}
                    onClick={() => handleToggleSelection(item.id)}
                    className={cn(
                      "cursor-pointer rounded-xl border bg-bg p-3 transition-colors",
                      checked ? "border-brand-500 bg-brand-50" : "border-border"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggleSelection(item.id)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-500"
                        aria-label={t("Seleccionar {{id}}", { id: item.followingNumber || item.id })}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-xs text-fg-muted">
                            {item.followingNumber || "—"}
                          </span>
                          <Badge tone="neutral">{item.statusLabel}</Badge>
                        </div>
                        <p className="mt-1 truncate text-sm font-semibold text-fg">
                          {item.receiverName || t("Sin nombre")}
                        </p>
                        <p className="mt-0.5 text-xs text-fg-muted">
                          {item.municipality || "—"} · {item.normalizedPhone || "—"}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH className="w-10" />
                  <TH>{t("Seguimiento")}</TH>
                  <TH>{t("Destinatario")}</TH>
                  <TH>{t("Comuna")}</TH>
                  <TH>{t("Estado")}</TH>
                  <TH>{t("Teléfono")}</TH>
                </TR>
              </THead>
              <TBody>
                {preview.items.map((item) => {
                  const checked = selectedIds.has(item.id);
                  return (
                    <TR
                      key={item.id}
                      onClick={() => handleToggleSelection(item.id)}
                      className={cn("cursor-pointer", checked && "bg-brand-50 hover:bg-brand-50")}
                    >
                      <TD onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleSelection(item.id)}
                          className="h-4 w-4 cursor-pointer accent-brand-500"
                          aria-label={t("Seleccionar {{id}}", { id: item.followingNumber || item.id })}
                        />
                      </TD>
                      <TD className="font-mono text-xs text-fg-muted">{item.followingNumber || "—"}</TD>
                      <TD>
                        <strong className="font-semibold text-fg">{item.receiverName || t("Sin nombre")}</strong>
                      </TD>
                      <TD>{item.municipality || "—"}</TD>
                      <TD>
                        <Badge tone="neutral">{item.statusLabel}</Badge>
                      </TD>
                      <TD>{item.normalizedPhone || "—"}</TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </Card>

        {/* Send card */}
        <Card className="flex flex-col gap-4 xl:sticky xl:top-4">
          <h3 className="text-base font-semibold text-fg">{t("Enviar por WhatsApp")}</h3>

          <BroadcastComposer
            numbers={numbers}
            value={message}
            onChange={(partial) => setMessage((prev) => ({ ...prev, ...partial }))}
            sampleItem={sampleItem}
            disabled={creating}
          />

          <div className="flex flex-col gap-2" role="radiogroup" aria-label={t("Destinatarios")}>
            <span className="text-xs font-semibold text-fg-muted">{t("Destinatarios")}</span>
            {SCOPES.map((option) => {
              const active = scope === option.id;
              const count = option.id === BROADCAST_SCOPES.SELECTION ? selectedCount : totalDocs;
              return (
                <label
                  key={option.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 rounded-xl border p-3 transition-colors",
                    active ? "border-brand-500 bg-brand-50" : "border-border bg-bg-muted"
                  )}
                >
                  <input
                    type="radio"
                    name="broadcast-scope"
                    value={option.id}
                    checked={active}
                    onChange={() => setScope(option.id)}
                    disabled={creating}
                    className="shrink-0 accent-brand-500"
                  />
                  <div>
                    <strong className="block text-sm font-semibold text-fg">
                      {option.label} {preview ? `(${count})` : ""}
                    </strong>
                    <small className="block text-xs text-fg-muted">{option.helper}</small>
                  </div>
                </label>
              );
            })}
          </div>

          <Button
            fullWidth
            disabled={!!blockedReason || creating}
            onClick={handleOpenConfirm}
            iconLeft={<Send className="w-4 h-4" />}
          >
            {t("Revisar y enviar")}
          </Button>
          {blockedReason && (
            <p className="m-0 text-center text-xs text-fg-muted" role="status">
              {blockedReason}
            </p>
          )}
          <p className="m-0 text-xs text-fg-muted">
            {t("Se envía de 09:00 a 23:00 (hora de Chile), hasta {{cap}} mensajes por número al día. Lo que no alcance sigue al día siguiente.", { cap: ESTIMATE_DAILY_CAP })}
          </p>
        </Card>
      </div>

      {/* ── Excluded ── */}
      {preview?.excluded?.length ? (
        <div className="rounded-xl border border-status-warning/30 bg-status-warning-soft px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-status-warning">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <strong>{preview.excluded.length} {t("paquetes serán excluidos")}</strong>
              <span className="text-status-warning/80">— {t("sin teléfono válido u otras razones")}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowExcluded((v) => !v)}>
              {showExcluded ? t("Ocultar") : t("Ver detalle")}
            </Button>
          </div>

          {showExcluded && (
            <ul className="m-0 mt-3 flex list-none flex-col p-0">
              {preview.excluded.slice(0, 10).map((item) => (
                <li
                  key={item.id}
                  className="flex flex-col gap-0.5 border-b border-status-warning/20 py-2 last:border-b-0"
                >
                  <strong className="text-sm text-fg">{item.followingNumber || item.id}</strong>
                  <span className="text-xs text-status-warning">{item.reasons.map((r) => r.message).join(", ")}</span>
                </li>
              ))}
              {preview.excluded.length > 10 && (
                <li className="py-2 text-xs italic text-status-warning">
                  +{preview.excluded.length - 10} {t("más...")}
                </li>
              )}
            </ul>
          )}
        </div>
      ) : null}

      <BroadcastActivity
        activeId={activeBroadcastId}
        onSelect={setActiveBroadcastId}
        refreshToken={historyToken}
      />
    </div>
  );
}
